import {
  addDays,
  differenceInDays,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  parseISO,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';
import { SYSTEM_SETTINGS_DEFAULTS } from '../../constants/systemSettingsDefaults';

export const formatTime = (seconds) => {
  const hours = Math.floor((seconds || 0) / 3600);
  const minutes = Math.floor(((seconds || 0) % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

export const formatHours = (seconds) => ((seconds || 0) / 3600).toFixed(1);

export const formatCurrency = (
  amount,
  currencyCode = SYSTEM_SETTINGS_DEFAULTS.currency_code
) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode || SYSTEM_SETTINGS_DEFAULTS.currency_code,
  }).format(amount || 0);

export const formatPercentage = (value, total) => {
  if (!total) return '0%';
  return `${((value / total) * 100).toFixed(1)}%`;
};

export const formatTrendLabel = (deltaPct) => {
  if (deltaPct == null || Number.isNaN(deltaPct)) return '0%';
  const sign = deltaPct > 0 ? '+' : '';
  return `${sign}${deltaPct.toFixed(1)}%`;
};

export const resolveDateRange = (dateRange, now = new Date()) => {
  let start = '';
  let end = '';
  switch (dateRange) {
    case 'today':
      start = end = format(now, 'yyyy-MM-dd');
      break;
    case 'week':
      start = format(startOfWeek(now), 'yyyy-MM-dd');
      end = format(endOfWeek(now), 'yyyy-MM-dd');
      break;
    case 'month':
      start = format(startOfMonth(now), 'yyyy-MM-dd');
      end = format(endOfMonth(now), 'yyyy-MM-dd');
      break;
    case 'last7':
      start = format(subDays(now, 7), 'yyyy-MM-dd');
      end = format(now, 'yyyy-MM-dd');
      break;
    case 'last30':
      start = format(subDays(now, 30), 'yyyy-MM-dd');
      end = format(now, 'yyyy-MM-dd');
      break;
    case 'quarter': {
      const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      const quarterEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
      start = format(quarterStart, 'yyyy-MM-dd');
      end = format(quarterEnd, 'yyyy-MM-dd');
      break;
    }
    case 'year':
      start = format(new Date(now.getFullYear(), 0, 1), 'yyyy-MM-dd');
      end = format(new Date(now.getFullYear(), 11, 31), 'yyyy-MM-dd');
      break;
    default:
      break;
  }
  return { start, end };
};

export const previousPeriodBounds = (startDate, endDate) => {
  if (!startDate || !endDate) return { prevStart: '', prevEnd: '' };
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const days = Math.max(differenceInDays(end, start) + 1, 1);
  const prevEnd = subDays(start, 1);
  const prevStart = subDays(prevEnd, days - 1);
  return {
    prevStart: format(prevStart, 'yyyy-MM-dd'),
    prevEnd: format(prevEnd, 'yyyy-MM-dd'),
  };
};

export const calculateTrendDelta = (current, previous) => {
  if (previous === 0) {
    const pct = current > 0 ? 100 : 0;
    return {
      current,
      previous,
      deltaPct: pct,
      direction: current > previous ? 'up' : current < previous ? 'down' : 'flat',
    };
  }
  const deltaPct = ((current - previous) / Math.abs(previous)) * 100;
  return {
    current,
    previous,
    deltaPct,
    direction: current > previous ? 'up' : current < previous ? 'down' : 'flat',
  };
};

export const calculateKPIs = ({
  timeEntries = [],
  projects = [],
  tasks = [],
  teamMembers = [],
  analytics = null,
  weeklyTargetHours = 40,
}) => {
  const totalSeconds = timeEntries.reduce((sum, entry) => sum + (entry.duration || 0), 0);
  const billableSeconds = timeEntries
    .filter((entry) => entry.is_billable)
    .reduce((sum, entry) => sum + (entry.duration || 0), 0);

  const totalRevenue = projects.reduce((sum, project) => {
    const projectEntries = timeEntries.filter((entry) => entry.project_id === project.id);
    const projectHours =
      projectEntries.reduce((acc, entry) => acc + (entry.duration || 0), 0) / 3600;
    return sum + projectHours * (project.hourly_rate || 0);
  }, 0);

  const avgProductivity = analytics?.avgDailyHours || 0;
  const utilizationRate = analytics
    ? (analytics.totalHours / Math.max(analytics.totalUsers || teamMembers.length || 1, 1) / weeklyTargetHours) *
      100
    : teamMembers.length
      ? (totalSeconds / 3600 / (teamMembers.length * weeklyTargetHours)) * 100
      : 0;

  return {
    totalHours: totalSeconds / 3600,
    billableHours: billableSeconds / 3600,
    totalRevenue,
    avgProductivity,
    utilizationRate,
    totalProjects: projects.length,
    totalTasks: tasks.length,
    totalUsers: teamMembers.length,
    billablePercentage: totalSeconds > 0 ? (billableSeconds / totalSeconds) * 100 : 0,
  };
};

export const calculateProjectMetrics = (projects = [], timeEntries = [], tasks = []) =>
  projects
    .map((project) => {
      const projectEntries = timeEntries.filter((entry) => entry.project_id === project.id);
      const projectTasks = tasks.filter((task) => task.project_id === project.id);
      const totalTime = projectEntries.reduce((sum, entry) => sum + (entry.duration || 0), 0);
      const billableTime = projectEntries
        .filter((entry) => entry.is_billable)
        .reduce((sum, entry) => sum + (entry.duration || 0), 0);
      const completedTasks = projectTasks.filter((task) => task.status === 'Done').length;
      const overdueTasks = projectTasks.filter((task) => {
        if (!task.due_date || task.status === 'Done') return false;
        return isAfter(new Date(), parseISO(task.due_date));
      }).length;
      const revenue = (billableTime / 3600) * (project.hourly_rate || 0);
      const completionRate =
        projectTasks.length > 0 ? (completedTasks / projectTasks.length) * 100 : 0;
      return {
        ...project,
        totalTime,
        billableTime,
        revenue,
        completedTasks,
        overdueTasks,
        totalTasks: projectTasks.length,
        completionRate,
        efficiency: totalTime > 0 ? (billableTime / totalTime) * 100 : 0,
      };
    })
    .sort((a, b) => b.revenue - a.revenue);

export const calculateTeamMetrics = (teamMembers = [], timeEntries = [], tasks = [], startDate) =>
  teamMembers
    .map((member) => {
      const memberEntries = timeEntries.filter((entry) => entry.user_id === member.id);
      const memberTasks = tasks.filter((task) => task.assigned_to === member.id);
      const totalTime = memberEntries.reduce((sum, entry) => sum + (entry.duration || 0), 0);
      const billableTime = memberEntries
        .filter((entry) => entry.is_billable)
        .reduce((sum, entry) => sum + (entry.duration || 0), 0);
      const completedTasks = memberTasks.filter((task) => task.status === 'Done').length;
      const overdueTasks = memberTasks.filter((task) => {
        if (!task.due_date || task.status === 'Done') return false;
        return isAfter(new Date(), parseISO(task.due_date));
      }).length;
      const productivity = totalTime / 3600;
      const efficiency = totalTime > 0 ? (billableTime / totalTime) * 100 : 0;
      const taskCompletionRate =
        memberTasks.length > 0 ? (completedTasks / memberTasks.length) * 100 : 0;
      const daySpan =
        startDate && !Number.isNaN(Date.parse(startDate))
          ? Math.max(1, differenceInDays(new Date(), parseISO(startDate)))
          : 1;
      return {
        ...member,
        totalTime,
        billableTime,
        productivity,
        efficiency,
        completedTasks,
        overdueTasks,
        totalTasks: memberTasks.length,
        taskCompletionRate,
        avgDailyHours: productivity / daySpan,
      };
    })
    .sort((a, b) => b.productivity - a.productivity);

export const calculateDailyTrends = (timeEntries = [], startDate, endDate) => {
  if (!startDate || !endDate) return [];
  const days = differenceInDays(parseISO(endDate), parseISO(startDate));
  if (Number.isNaN(days) || days < 0) return [];
  const dailyData = [];
  for (let i = 0; i <= days; i += 1) {
    const date = addDays(parseISO(startDate), i);
    const dayKey = format(date, 'yyyy-MM-dd');
    const dayEntries = timeEntries.filter((entry) => {
      if (!entry.start_time) return false;
      return format(parseISO(entry.start_time), 'yyyy-MM-dd') === dayKey;
    });
    const dayHours = dayEntries.reduce((sum, entry) => sum + (entry.duration || 0), 0) / 3600;
    const billableHours =
      dayEntries
        .filter((entry) => entry.is_billable)
        .reduce((sum, entry) => sum + (entry.duration || 0), 0) / 3600;
    dailyData.push({
      date: dayKey,
      totalHours: dayHours,
      billableHours,
      entries: dayEntries.length,
    });
  }
  return dailyData;
};

export const sumEntryHours = (entries = []) =>
  entries.reduce((sum, entry) => sum + (entry.duration || 0), 0) / 3600;

export const sumRevenue = (entries = [], projects = []) =>
  entries.reduce((sum, entry) => {
    const project = projects.find((p) => p.id === entry.project_id);
    return sum + ((entry.duration || 0) / 3600) * (project?.hourly_rate || 0);
  }, 0);

export const filterLeadProjects = (projects = [], userId) =>
  projects.filter(
    (project) =>
      project.created_by === userId ||
      (Array.isArray(project.member_ids) && project.member_ids.includes(userId))
  );

export const getAvailableViews = (role) => {
  if (role === 'admin') {
    return [
      { id: 'executive', label: 'Executive Dashboard' },
      { id: 'financial', label: 'Financial Reports' },
      { id: 'team-performance', label: 'Team Performance' },
      { id: 'project-health', label: 'Project Health' },
      { id: 'custom', label: 'Custom Reports' },
    ];
  }
  if (role === 'project_lead') {
    return [
      { id: 'member', label: 'Personal Productivity' },
      { id: 'project-status', label: 'My Projects Status' },
      { id: 'custom', label: 'Custom Reports' },
    ];
  }
  return [
    { id: 'member', label: 'Personal Productivity' },
    { id: 'custom', label: 'Custom Reports' },
  ];
};
